import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLang } from '../i18n/LanguageContext';
import api from '../lib/api';
import { getMergedCourses } from '../lib/courses';
import { cacheCourses } from '../lib/courseCache';
import { CourseCard } from './courses/CourseCard';

const NavArrow = ({ side, disabled, onClick, Icon, testId }) => (
  <button type="button" data-testid={testId} onClick={onClick} disabled={disabled} aria-label={side}
    className={`hidden md:flex absolute top-1/2 -translate-y-1/2 ${side === 'left' ? '-left-5' : '-right-5'} z-10 w-11 h-11 rounded-full bg-white border border-slate-200 text-slate-700 items-center justify-center shadow-lg hover:bg-slate-900 hover:text-white hover:border-slate-900 disabled:opacity-0 disabled:pointer-events-none transition-[opacity,background-color,color,border-color]`}>
    <Icon className="w-5 h-5" />
  </button>
);

const CoursesSection = () => {
  const { t, isRTL } = useLang();
  const navigate = useNavigate();
  const Arrow = isRTL ? ArrowLeft : ArrowRight;
  const [courses, setCourses] = useState(getMergedCourses([]));
  const track = useRef(null);
  const [edges, setEdges] = useState({ left: true, right: true });

  useEffect(() => {
    api.get('/courses').then(({ data }) => { cacheCourses(data); setCourses(getMergedCourses(data).filter(c => c.show_home !== false)); }).catch(() => {});
  }, []);

  const update = useCallback(() => {
    const el = track.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const pos = Math.abs(el.scrollLeft);
    const atStart = pos <= 20;
    const atEnd = pos >= max - 20;
    setEdges(isRTL ? { right: atStart, left: atEnd } : { left: atStart, right: atEnd });
  }, [isRTL]);

  useEffect(() => {
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [update, courses]);

  const scroll = (dir) => {
    const el = track.current;
    const card = el?.firstElementChild;
    if (!card) return;
    el.scrollBy({ left: dir * (card.offsetWidth + 24), behavior: 'smooth' });
  };

  return (
    <section data-testid="home-courses" className="section-y bg-white">
      <div className="site-container">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 section-head">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-amber-600 mb-2">{t.nav.courses}</div>
            <h2 className="text-2xl sm:text-3xl lg:text-[2rem] font-black text-slate-900 tracking-tight mb-2">{t.sections.latestCourses}</h2>
            <p className="text-slate-500 text-sm md:text-base">{t.sections.latestCoursesDesc}</p>
          </div>
          <Link to="/courses" data-testid="home-courses-view-all" className="group inline-flex items-center gap-2 text-blue-700 font-bold text-sm px-4 py-2.5 rounded-xl bg-blue-50 hover:bg-blue-100 transition-colors">
            {t.sections.viewAll}
            <Arrow className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>
        <div className="relative">
          <NavArrow side="right" testId="home-courses-arrow-right" disabled={edges.right} onClick={() => scroll(1)} Icon={ChevronRight} />
          <NavArrow side="left" testId="home-courses-arrow-left" disabled={edges.left} onClick={() => scroll(-1)} Icon={ChevronLeft} />
          <div ref={track} onScroll={update} data-testid="home-courses-track"
            className="flex gap-6 overflow-x-auto snap-x snap-mandatory scrollbar-none pt-4 pb-8 px-4 -mx-4">
            {courses.map(c => (
              <div key={c.id} className="snap-start shrink-0 w-[85%] sm:w-[calc((100%-1.5rem)/2)] lg:w-[calc((100%-3rem)/3)]">
                <CourseCard course={c} onOpen={(id) => navigate(`/courses/${id}`)} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CoursesSection;
